import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
    static targets = ["hiddenBadge"]

    connect() {
        this.gridName = this.element.dataset.gridName
        this.preferences = this.parsePreferences()
        this.table = this.ownedElements("table")[0] || this.element

        this.setupTableScroll()
        this.setupFilterCollapsePreference()
        this.setupColumnPreferences()
        this.setupExport()
    }

    disconnect() {
        if (this.tableScrollContainer && this.boundUpdateTableScrollState) {
            this.tableScrollContainer.removeEventListener("scroll", this.boundUpdateTableScrollState)
        }

        if (this.filterCollapse) {
            this.filterCollapse.removeEventListener("shown.bs.collapse", this.boundFiltersShown)
            this.filterCollapse.removeEventListener("hidden.bs.collapse", this.boundFiltersHidden)
        }

        if (this.exportButton && this.boundExportCsv) {
            this.exportButton.removeEventListener("click", this.boundExportCsv)
        }

        clearTimeout(this.preferenceSaveTimeout)
        this.columnSortable?.destroy()
        this.defaultColumnSortable?.destroy()
        this.tableScrollResizeObserver?.disconnect()
    }

    ownedElements(selector) {
        return Array.from(this.element.querySelectorAll(selector)).filter(element => {
            return element.closest('[data-controller~="datagrid"]') === this.element
        })
    }

    parsePreferences() {
        try {
            return JSON.parse(this.element.dataset.userPrefs || "{}")
        } catch (error) {
            console.warn("Impossible de lire les préférences de la grid :", error)
            return {}
        }
    }

    csrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content || ""
    }

    setupFilterCollapsePreference() {
        const collapse = this.ownedElements("#filtersCollapse")[0]
        if (!collapse) return

        this.filterCollapse = collapse
        this.boundFiltersShown = () => this.saveFiltersExpandedPreference(true)
        this.boundFiltersHidden = () => this.saveFiltersExpandedPreference(false)
        collapse.addEventListener("shown.bs.collapse", this.boundFiltersShown)
        collapse.addEventListener("hidden.bs.collapse", this.boundFiltersHidden)
    }

    async saveFiltersExpandedPreference(filtersExpanded) {
        await this.updateUserPreference({ filters_expanded: filtersExpanded })
    }

    setupColumnPreferences() {
        this.columnList = this.ownedElements("[data-datagrid-column-list]")[0]
        this.defaultColumnList = this.ownedElements("[data-datagrid-default-column-list]")[0]
        if (!this.columnList) return

        this.columnSortable = this.createColumnSortable(this.columnList, () => {
            this.normalizeStickyConfiguration(this.columnList)
            this.applyUserColumnConfiguration()
            this.scheduleUserPreferenceSave()
        })

        if (this.defaultColumnList) {
            this.defaultColumnSortable = this.createColumnSortable(this.defaultColumnList, () => {
                this.normalizeStickyConfiguration(this.defaultColumnList)
                this.refreshStickyControls(this.defaultColumnList)
            })
            this.normalizeStickyConfiguration(this.defaultColumnList)
        }

        this.normalizeStickyConfiguration(this.columnList)
        this.applyUserColumnConfiguration()
        this.updateBadge()
    }

    createColumnSortable(list, onEnd) {
        return Sortable.create(list, {
            animation: 120,
            draggable: ".datagrid-column-preference-item",
            handle: ".datagrid-column-drag-handle",
            ghostClass: "datagrid-column-preference-item--ghost",
            chosenClass: "datagrid-column-preference-item--chosen",
            onEnd: event => {
                this.inheritDroppedStickySide(list, event)
                onEnd(event)
            }
        })
    }

    inheritDroppedStickySide(list, event) {
        if (!event.item || event.oldIndex === event.newIndex) return

        const visibleItems = this.columnItems(list).filter(item => this.visibilityCheckbox(item)?.checked)
        const index = visibleItems.indexOf(event.item)
        if (index === -1) return

        const remainingItems = visibleItems.filter(item => item !== event.item)
        let leftEnd = 0
        let rightStart = remainingItems.length

        while (remainingItems[leftEnd]?.dataset.sticky === "left") leftEnd += 1
        while (rightStart > 0 && remainingItems[rightStart - 1].dataset.sticky === "right") rightStart -= 1

        if (index < leftEnd) {
            event.item.dataset.sticky = "left"
        } else if (index > rightStart) {
            event.item.dataset.sticky = "right"
        }
    }

    columnItems(list = this.columnList) {
        return list ? Array.from(list.querySelectorAll(":scope > .datagrid-column-preference-item")) : []
    }

    visibilityCheckbox(item) {
        return item.querySelector(".datagrid-column-visibility-checkbox")
    }

    stickyCheckbox(item, side) {
        return item.querySelector(`[data-sticky-side="${side}"]`)
    }

    columnConfiguration(list = this.columnList) {
        return {
            version: 1,
            columns: this.columnItems(list).map(item => ({
                name: item.dataset.columnName,
                visible: this.visibilityCheckbox(item)?.checked === true,
                sticky: item.dataset.sticky || null
            }))
        }
    }

    normalizeStickyConfiguration(list) {
        const visibleItems = this.columnItems(list).filter(item => this.visibilityCheckbox(item)?.checked)
        let leftBoundaryOpen = true

        visibleItems.forEach(item => {
            if (item.dataset.sticky === "left" && leftBoundaryOpen) return

            leftBoundaryOpen = false
            if (item.dataset.sticky === "left") item.dataset.sticky = ""
        })

        let rightBoundaryOpen = true
        visibleItems.reverse().forEach(item => {
            if (item.dataset.sticky === "right" && rightBoundaryOpen) return

            rightBoundaryOpen = false
            if (item.dataset.sticky === "right") item.dataset.sticky = ""
        })

        this.syncStickyCheckboxes(list)
    }

    syncStickyCheckboxes(list) {
        this.columnItems(list).forEach(item => {
            const leftCheckbox = this.stickyCheckbox(item, "left")
            const rightCheckbox = this.stickyCheckbox(item, "right")
            if (leftCheckbox) leftCheckbox.checked = item.dataset.sticky === "left"
            if (rightCheckbox) rightCheckbox.checked = item.dataset.sticky === "right"
        })

        this.refreshStickyControls(list)
    }

    refreshStickyControls(list) {
        const items = this.columnItems(list)
        const visibleItems = items.filter(item => this.visibilityCheckbox(item)?.checked)
        const leftPrefixLength = visibleItems.findIndex(item => item.dataset.sticky !== "left")
        const normalizedLeftLength = leftPrefixLength === -1 ? visibleItems.length : leftPrefixLength
        const firstRightIndex = visibleItems.findIndex(item => item.dataset.sticky === "right")
        const rightBoundary = firstRightIndex === -1 ? visibleItems.length : firstRightIndex

        items.forEach(item => {
            const visible = this.visibilityCheckbox(item)?.checked === true
            const visibleIndex = visibleItems.indexOf(item)
            const leftCheckbox = this.stickyCheckbox(item, "left")
            const rightCheckbox = this.stickyCheckbox(item, "right")

            if (leftCheckbox) {
                const isLeft = item.dataset.sticky === "left"
                leftCheckbox.disabled = !visible || (isLeft
                    ? visibleIndex !== normalizedLeftLength - 1
                    : item.dataset.sticky === "right" || visibleIndex !== normalizedLeftLength)
            }

            if (rightCheckbox) {
                const isRight = item.dataset.sticky === "right"
                rightCheckbox.disabled = !visible || (isRight
                    ? visibleIndex !== rightBoundary
                    : item.dataset.sticky === "left" || visibleIndex !== rightBoundary - 1)
            }
        })
    }

    toggleColumn(event) {
        const item = event.currentTarget.closest(".datagrid-column-preference-item")
        if (!item) return

        this.normalizeStickyConfiguration(this.columnList)
        this.applyUserColumnConfiguration()
        this.updateBadge()
        this.scheduleUserPreferenceSave()
    }

    toggleDefaultColumn() {
        this.normalizeStickyConfiguration(this.defaultColumnList)
        this.refreshStickyControls(this.defaultColumnList)
    }

    toggleSticky(event) {
        this.updateStickyChoice(event, this.columnList)
        this.applyUserColumnConfiguration()
        this.scheduleUserPreferenceSave()
    }

    toggleDefaultSticky(event) {
        this.updateStickyChoice(event, this.defaultColumnList)
    }

    updateStickyChoice(event, list) {
        const checkbox = event.currentTarget
        const item = checkbox.closest(".datagrid-column-preference-item")
        if (!item) return

        const side = checkbox.dataset.stickySide
        item.dataset.sticky = checkbox.checked ? side : ""

        if (checkbox.checked) {
            const oppositeSide = side === "left" ? "right" : "left"
            const oppositeCheckbox = this.stickyCheckbox(item, oppositeSide)
            if (oppositeCheckbox) oppositeCheckbox.checked = false
        }

        this.normalizeStickyConfiguration(list)
        this.refreshStickyControls(list)
    }

    applyUserColumnConfiguration() {
        if (!(this.table instanceof HTMLTableElement)) return

        const items = this.columnItems(this.columnList)
        this.reorderTableColumns(items.map(item => item.dataset.columnName))

        items.forEach(item => {
            const name = item.dataset.columnName
            const visible = this.visibilityCheckbox(item)?.checked === true
            this.cellsForColumn(name).forEach(cell => {
                cell.style.display = visible ? "" : "none"
                cell.classList.remove("datagrid-sticky-left", "datagrid-sticky-right")
                if (item.dataset.sticky) cell.classList.add(`datagrid-sticky-${item.dataset.sticky}`)
            })
        })

        this.updateStickyColumns()
        this.updateTableScrollState()
    }

    reorderTableColumns(columnNames) {
        const rows = [
            ...Array.from(this.table.tHead?.rows || []),
            ...Array.from(this.table.tBodies).flatMap(body => Array.from(body.rows))
        ]

        rows.forEach(row => {
            const cells = Array.from(row.cells)
            const cellsByName = new Map(cells.map(cell => [cell.dataset.column, cell]))

            columnNames.forEach(name => {
                const cell = cellsByName.get(name)
                if (cell) row.appendChild(cell)
            })

            cells.forEach(cell => {
                if (!columnNames.includes(cell.dataset.column)) row.appendChild(cell)
            })
        })
    }

    cellsForColumn(name) {
        return Array.from(this.table.querySelectorAll("[data-column]")).filter(cell => {
            return cell.dataset.column === name
        })
    }

    updateBadge() {
        if (!this.hasHiddenBadgeTarget || !this.columnList) return

        const items = this.columnItems(this.columnList)
        const visibleCount = items.filter(item => this.visibilityCheckbox(item)?.checked).length
        this.hiddenBadgeTarget.textContent = `${visibleCount}/${items.length}`
    }

    scheduleUserPreferenceSave() {
        clearTimeout(this.preferenceSaveTimeout)
        this.preferenceSaveTimeout = setTimeout(() => this.saveUserPreference(), 150)
    }

    async saveUserPreference() {
        if (!this.columnList) return

        this.preferences.visible_columns = this.columnConfiguration(this.columnList)
        await this.updateUserPreference({
            visible_columns: this.preferences.visible_columns,
            badge_color: this.preferences.badge_color,
            per_page: this.preferences.per_page
        })
    }

    async updateUserPreference(attributes) {
        const body = {
            datagrid_user_preference: {
                grid_name: this.gridName,
                ...attributes
            }
        }

        try {
            const response = await fetch("/backoffice/datagrid_user_preferences/update_preference", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": this.csrfToken()
                },
                body: JSON.stringify(body)
            })

            if (!response.ok) throw new Error(`HTTP ${response.status}`)
        } catch (error) {
            console.warn("Erreur de sauvegarde des préférences utilisateur :", error)
        }
    }

    setupTableScroll() {
        const container = this.ownedElements(".datagrid-table-scroll")[0]
        if (!container) return

        this.tableScrollContainer = container
        this.boundUpdateTableScrollState = this.updateTableScrollState.bind(this)
        container.addEventListener("scroll", this.boundUpdateTableScrollState, { passive: true })

        this.tableScrollResizeObserver = new ResizeObserver(this.boundUpdateTableScrollState)
        this.tableScrollResizeObserver.observe(container)
        if (this.table instanceof HTMLTableElement) this.tableScrollResizeObserver.observe(this.table)

        requestAnimationFrame(this.boundUpdateTableScrollState)
    }

    updateTableScrollState() {
        if (!this.tableScrollContainer) return

        const container = this.tableScrollContainer
        const tolerance = 1
        this.updateStickyColumns()

        const scrollable = container.scrollWidth > container.clientWidth + tolerance
        const scrolledFromStart = scrollable && container.scrollLeft > tolerance
        const scrolledToEnd = !scrollable ||
            container.scrollLeft + container.clientWidth >= container.scrollWidth - tolerance

        container.classList.toggle("datagrid-table-scroll--scrollable", scrollable)
        container.classList.toggle("datagrid-table-scroll--scrolled-from-start", scrolledFromStart)
        container.classList.toggle("datagrid-table-scroll--at-end", scrolledToEnd)
    }

    updateStickyColumns() {
        this.updateStickySide("left")
        this.updateStickySide("right")
    }

    updateStickySide(side) {
        if (!(this.table instanceof HTMLTableElement)) return

        const headerRow = this.table.tHead?.rows[0]
        if (!headerRow) return

        const headers = Array.from(headerRow.cells)
        const orderedHeaders = side === "right" ? headers.reverse() : headers
        const bodyRows = Array.from(this.table.tBodies).flatMap(body => Array.from(body.rows))
        const overlap = Number.parseFloat(
            window.getComputedStyle(this.table).getPropertyValue("--datagrid-sticky-overlap")
        ) || 0
        const offsetProperty = `--datagrid-sticky-${side}-offset`
        const boundaryClass = `datagrid-sticky-${side}--last`
        let offset = 0
        let stickyPosition = 0
        let boundaryIndex = null

        this.table.querySelectorAll(`.datagrid-sticky-${side}`).forEach(cell => {
            cell.style.removeProperty(offsetProperty)
            cell.classList.remove(boundaryClass)
        })

        orderedHeaders.forEach(header => {
            if (!header.classList.contains(`datagrid-sticky-${side}`)) return
            if (window.getComputedStyle(header).display === "none") return

            const index = Array.from(headerRow.cells).indexOf(header)
            const columnCells = [
                header,
                ...bodyRows.map(row => row.cells[index]).filter(Boolean)
            ]
            const stickyOffset = offset - (stickyPosition + 1) * overlap

            columnCells.forEach(cell => cell.style.setProperty(offsetProperty, `${stickyOffset}px`))
            offset += header.getBoundingClientRect().width
            boundaryIndex = index
            stickyPosition += 1
        })

        if (boundaryIndex === null) return

        const boundaryCells = [
            headerRow.cells[boundaryIndex],
            ...bodyRows.map(row => row.cells[boundaryIndex]).filter(Boolean)
        ]
        boundaryCells.forEach(cell => cell.classList.add(boundaryClass))
    }

    setupExport() {
        const exportButton = this.ownedElements("#export-csv-btn")[0]
        if (!exportButton) return

        this.exportButton = exportButton
        this.boundExportCsv = this.exportCsv.bind(this)
        exportButton.addEventListener("click", this.boundExportCsv)
    }

    exportCsv(event) {
        event.preventDefault()

        const configuration = this.columnList
            ? this.columnConfiguration(this.columnList)
            : this.preferences.visible_columns
        const visibleNames = configuration?.columns
            .filter(column => column.visible)
            .map(column => column.name) || []
        const exportUrl = new URL(event.currentTarget.dataset.exportUrl, window.location.origin)
        exportUrl.searchParams.set("visible_columns", visibleNames.join(","))
        window.location.href = exportUrl.toString()
    }

    updateBadgeColor(event) {
        const color = event.target.value
        if (this.hasHiddenBadgeTarget) this.hiddenBadgeTarget.style.backgroundColor = color
        this.preferences.badge_color = color
        this.scheduleUserPreferenceSave()
    }

    async setPerPage(event) {
        event.preventDefault()
        this.preferences.per_page = Number.parseInt(event.currentTarget.dataset.value, 10)
        await this.saveUserPreference()
        window.location.reload()
    }

    async saveDefaultPreferences(event) {
        const modal = event.currentTarget.closest(".modal")
        if (!modal || !this.defaultColumnList) return

        const body = {
            grid_name: modal.dataset.gridName,
            visible_columns: this.columnConfiguration(this.defaultColumnList),
            badge_color: modal.querySelector('input[type="color"]')?.value,
            per_page: modal.querySelector("select")?.value
        }

        try {
            const response = await fetch(
                `/backoffice/datagrid_default_preferences/${this.gridName}/update_preference`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": this.csrfToken()
                    },
                    body: JSON.stringify(body)
                }
            )

            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            window.location.reload()
        } catch (error) {
            console.warn("Erreur de sauvegarde des préférences globales :", error)
        }
    }

    async resetPreferences() {
        try {
            const response = await fetch(`/backoffice/datagrid_user_preferences/reset?grid_name=${this.gridName}`, {
                method: "DELETE",
                headers: { "X-CSRF-Token": this.csrfToken() }
            })

            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            window.location.reload()
        } catch (error) {
            console.warn("Impossible de réinitialiser les préférences :", error)
        }
    }
}
